import { useSuspenseQuery } from '@tanstack/react-query';
import { SourcingService } from 'api-clients/monolith';
import _range from 'lodash/range';
import { Suspense, useEffect, useMemo, useState } from 'react';

import { useJobPostingContext } from '@/contexts/job-posting-context';

import { JobCard } from './job-card';
import { RefreshJobDataBanner } from './refresh-job-data-banner';
import { SummaryView } from './summary-view';

function JobList() {
  const { selectedOpeningIds, hookForm } = useJobPostingContext();
  const { setValue, getValues, watch } = hookForm;
  const jobDetailsList = watch('jobDetails');
  const [showRefreshBanner, setShowRefreshBanner] = useState(false);

  const {
    data: { openings },
  } = useSuspenseQuery({
    queryKey: ['jobDetails', selectedOpeningIds],
    queryFn: () =>
      SourcingService.postInternalApiSourcingOpeningsOpeningsByExternalIds({
        opening_external_ids: selectedOpeningIds,
      }),
  });

  useEffect(() => {
    const existingJobDetails = getValues('jobDetails');

    // Only set initial values if jobDetails is not already populated
    if (openings.length > 0 && !existingJobDetails) {
      setValue(
        'jobDetails',
        openings.reduce((acc, opening) => {
          return {
            ...acc,
            [opening.external_id]: {
              title: opening.title,
              description: opening.job_description ?? '',
              isExpanded: openings.length === 1,
            },
          };
        }, {}),
        { shouldValidate: true },
      );
    }
  }, [openings, setValue, getValues]);

  /** Filter the openings to remove the ones that are deleted from the form state */
  const filteredOpenings = useMemo(() => {
    return openings.filter(opening =>
      Object.keys(jobDetailsList ?? {}).includes(opening.external_id),
    );
  }, [openings, jobDetailsList]);

  return (
    <>
      {showRefreshBanner && (
        <RefreshJobDataBanner onClose={() => setShowRefreshBanner(false)} />
      )}

      {filteredOpenings.map(opening => (
        <JobCard
          key={opening.external_id}
          opening={opening}
          onRequestRefreshBanner={() => setShowRefreshBanner(true)}
        />
      ))}
    </>
  );
}

export function JobDetails() {
  return (
    <Suspense
      fallback={
        <>
          {_range(10).map(index => (
            <SummaryView.Skeleton key={index} />
          ))}
        </>
      }
    >
      <JobList />
    </Suspense>
  );
}
